<template>
  <section class="settings-page">

    <!-- HEADER -->
    <header class="settings-header">
      <h1>Account Settings</h1>
      <p>Manage your profile, security, and privacy preferences.</p>
    </header>

    <!-- MAIN GRID -->
    <section class="settings-grid">

      <!-- LEFT COLUMN -->
      <div class="space-y">

        <!-- PRIVACY & DATA -->
        <div class="card">
          <h3>Privacy & Data</h3>

          <div class="toggle-row">
            <div>
              <strong>Data Usage Consent</strong>
              <p class="muted">
                Allow anonymized use of your data for aggregated insights
              </p>
            </div>
            <input type="checkbox" v-model="privacy.consent" />
          </div>

          <div class="toggle-row">
            <div>
              <strong>Profile Completion Reminders</strong>
              <p class="muted">
                Receive reminders to complete your profile
              </p>
            </div>
            <input type="checkbox" v-model="privacy.reminders" />
          </div>
        </div>

         <div class="card highlight">
          <h3>Save Changes</h3>
          <p class="muted">
            Your settings are private and can be updated anytime.
          </p>

          <button class="primary-btn full" @click="saveSettings">
            {{isLoading?"Saving...":"Save Settings"}}
          </button>
        </div>
      </div>

      <!-- RIGHT COLUMN -->
      <div class="space-y">

        <!-- SECURITY -->
        <div class="card">
          <h3>Security</h3>

          <div class="toggle-row">
            <div>
              <strong>Email</strong>
              <p v-if="userDt!=null" class="muted">{{userDt.user_email}}</p>
            </div>
            <span class="status verified">Verified</span>
          </div>

          <div class="toggle-row">
            <div>
              <strong>Phone</strong>
              <p v-if="userDt!=null" class="muted">{{userDt.user_mobile}}</p>
            </div>
            <span class="status verified">Verified</span>
          </div>

          <div class="toggle-row">
            <div>
              <strong>Password</strong>
              <p class="muted">Last updated 2 months ago</p>
            </div>
            <button class="link" @click="openPasswordModal">Change</button>
            <!-- <button class="link" @click="openProfileModal">Edit</button> -->
          </div>
        </div>

        <!-- NOTIFICATIONS -->
        <div class="card">
          <h3>Notifications</h3>

          <div class="toggle-row">
            <div>
              <strong>Important Updates</strong>
              <p class="muted">
                Account, security, and verification alerts
              </p>
            </div>
            <input type="checkbox" v-model="notifications.important" />
          </div>
        </div>

        <!-- SAVE -->
       

      </div>

    </section>
    <!-- PROFILE MODAL -->
    <div v-if="showProfileModal" class="modal-overlay">

      <div class="modal-card">
        <h3>Update Profile</h3>

        <div class="field">
          <label>Name</label>
          <input v-model="editProfile.user_name" type="text" />
        </div>

        <div class="field">
          <label>Mobile</label>
          <input v-model="editProfile.user_mobile" type="text" />
        </div>

        <div class="modal-actions">
          <button class="ghost-btn" @click="showProfileModal=false">
            Cancel
          </button>

          <button class="primary-btn" @click="updateProfile">
            {{isLoading ? "Saving..." : "Save"}}
          </button>
        </div>
      </div>

    </div>
    <!-- PASSWORD MODAL -->
    <div v-if="showPasswordModal" class="modal-overlay">

      <div class="modal-card">
        <h3>Change Password</h3>

        <div class="field">
          <label>Current Password</label>
          <input v-model="password.current" type="password" />
        </div>

        <div class="field">
          <label>New Password</label>
          <input v-model="password.new" type="password" />
        </div>

        <div class="field">
          <label>Confirm Password</label>
          <input v-model="password.confirm" type="password" />
        </div>

        <div class="modal-actions">
          <button class="ghost-btn" @click="showPasswordModal=false">
            Cancel
          </button>

          <button class="primary-btn" @click="changePassword">
            {{isLoading ? "Updating..." : "Update"}}
          </button>
        </div>
      </div>

    </div>
    
  </section>
</template>

<script>
  import { getFanSettings, saveFanSettings, updateFanProfile, changeFanPassword } from "../apis/fanapis";
  import { useToast } from "vue-toastification";

  export default {
    name: "SettingsAc",
    toast: null,
    data() {
      return {
        showProfileModal: false,
        showPasswordModal: false,

        editProfile: {
          user_name: "",
          user_mobile: ""
        },

        password: {
          current: "",
          new: "",
          confirm: ""
        },
        user_id: null,
        userDt: null,
        isLoading: false,
        interests: [
          "Technology",
          "Finance",
          "Fitness",
          "Education",
          "Travel",
          "Entertainment",
          "Marketing"
        ],

        profile: {
          occupation: "",
          education: "",
          interests: []
        },

        privacy: {
          consent: true,
          reminders: true
        },

        notifications: {
          important: true
        }
      };
    },

    mounted() {
      this.toast = useToast();
      const ldataRaw = sessionStorage.getItem("ldata");
      const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;

      if (ldata) {
        this.userDt = ldata;
        this.user_id = ldata.user_id;
        this.loadSettings();
      }
    },

    methods: {
      openProfileModal() {
        this.editProfile.user_name = this.userDt.user_name;
        this.editProfile.user_mobile = this.userDt.user_mobile;
        this.showProfileModal = true;
      },
      openPasswordModal() {
        this.password = { current: "", new: "", confirm: "" };
        this.showPasswordModal = true;
      },
      async updateProfile() {

        this.isLoading = true;

        const res = await updateFanProfile({
          user_id: this.user_id,
          user_name: this.editProfile.user_name,
          user_mobile: this.editProfile.user_mobile
        });

        this.isLoading = false;

        if (res.status) {

          this.userDt.user_name = this.editProfile.user_name;
          this.userDt.user_mobile = this.editProfile.user_mobile;

          sessionStorage.setItem("ldata", JSON.stringify(this.userDt));

          this.toast.success("Profile updated");
          this.showProfileModal = false;

        } else {
          this.toast.error(res.success || "Failed");
        }
      },
      async changePassword() {

        if (this.password.new !== this.password.confirm) {
          this.toast.error("Passwords do not match");
          return;
        }

        this.isLoading = true;

        const res = await changeFanPassword({
          user_id: this.user_id,
          current_password: this.password.current,
          new_password: this.password.new
        });

        this.isLoading = false;

        if (res.status) {

          this.toast.success("Password updated");
          this.showPasswordModal = false;

        } else {
          this.toast.error(res.success || "Invalid password");
        }
      },
      async loadSettings() {

        const res = await getFanSettings(this.user_id);

        if (res.status) {
          this.profile = res.profile;
          this.privacy = res.privacy;
          this.notifications = res.notifications;
        }
      },

      toggleInterest(i) {
        const idx = this.profile.interests.indexOf(i);
        if (idx >= 0) this.profile.interests.splice(idx, 1);
        else this.profile.interests.push(i);
      },

      async saveSettings() {
        this.isLoading = true;
        const res = await saveFanSettings({
          user_id: this.user_id,
          occupation: this.profile.occupation,
          education: this.profile.education,
          interests: this.profile.interests,
          consent: this.privacy.consent,
          reminders: this.privacy.reminders
        });
        this.isLoading = false;
        if (res.status) {
          this.toast.success("Settings saved successfully");
        }
        else {

          this.toast.error("Failed");
        }
      }
    }
  };
</script>

<style scoped>
  .settings-page {
    max-width: 1440px;
    margin: auto;
    padding: 48px;
  }

  .settings-header h1 {
    font-size: 36px;
    font-weight: 800;
  }

  .settings-header p {
    color: #64748b;
    margin-top: 6px;
  }

  /* GRID */
  .settings-grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 32px;
    margin-top: 32px;
  }

  .space-y>* {
    margin-bottom: 24px;
  }

  /* CARD */
  .card {
    background: white;
    border-radius: 24px;
    padding: 32px;
    border: 1px solid #e5e7eb;
  }

  .card h3 {
    font-size: 20px;
    font-weight: 800;
    margin-bottom: 24px;
  }

  .card.highlight {
    border: 2px solid #0f172a;
  }

  /* FIELD */
  .field label {
    font-size: 11px;
    text-transform: uppercase;
    color: #94a3b8;
    font-weight: 700;
  }

  .field select {
    width: 100%;
    margin-top: 6px;
    padding: 14px;
    border-radius: 14px;
    background: #f8fafc;
  }

  /* CHIPS */
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
  }

  .chips span {
    padding: 6px 12px;
    border-radius: 999px;
    background: #f1f5f9;
    font-weight: 700;
    cursor: pointer;
    font-size: 13px;
  }

  .chips span.active {
    background: #0f172a;
    color: white;
  }

  /* TOGGLES */
  .toggle-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 0;
    border-bottom: 1px solid #f1f5f9;
  }

  .toggle-row:last-child {
    border-bottom: none;
  }

  .muted {
    font-size: 13px;
    color: #94a3b8;
  }

  /* STATUS */
  .status {
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
  }

  .verified {
    background: #dcfce7;
    color: #166534;
  }

  /* BUTTONS */
  .primary-btn {
    background: #0f172a;
    color: white;
    padding: 16px 22px;
    border-radius: 14px;
    font-weight: 800;
  }

  .primary-btn.full {
    width: 100%;
    margin-top: 16px;
  }

  .link {
    background: none;
    font-weight: 800;
  }

  /* MOBILE */
  @media (max-width: 900px) {
    .settings-grid {
      grid-template-columns: 1fr;
    }
  }

  /* ===============================
   USER SETTINGS – MOBILE PATCH
   =============================== */
  @media (max-width: 640px) {

    /* Page padding */
    .settings-page {
      padding: 20px 16px 72px;
    }

    /* Header */
    .settings-header h1 {
      font-size: 26px;
      line-height: 1.2;
    }

    .settings-header p {
      font-size: 14px;
    }

    /* Grid already stacks at 900px – tighten spacing */
    .settings-grid {
      gap: 20px;
      margin-top: 20px;
    }

    /* Cards */
    .card {
      padding: 16px;
      border-radius: 18px;
    }

    .card h3 {
      font-size: 18px;
      margin-bottom: 16px;
    }

    /* Fields */
    .field select {
      padding: 12px;
      font-size: 14px;
      border-radius: 12px;
    }

    /* Chips → wrap better */
    .chips {
      gap: 6px;
    }

    .chips span {
      font-size: 12px;
      padding: 6px 10px;
    }

    /* Toggle rows → vertical-safe */
    .toggle-row {
      align-items: flex-start;
      gap: 12px;
    }

    .toggle-row>div {
      max-width: 75%;
    }

    .toggle-row strong {
      font-size: 14px;
    }

    .muted {
      font-size: 12px;
      line-height: 1.4;
    }

    /* Status pill */
    .status {
      font-size: 11px;
      padding: 4px 10px;
      white-space: nowrap;
    }

    /* Buttons */
    .primary-btn {
      padding: 14px 18px;
      font-size: 14px;
      border-radius: 12px;
    }

    .primary-btn.full {
      margin-top: 12px;
    }

    .link {
      font-size: 13px;
    }
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
  }

  .modal-card {
    width: 420px;
    background: white;
    border-radius: 20px;
    padding: 28px;
    border: 1px solid #e5e7eb;
    animation: pop 0.2s ease;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 18px;
  }

  .ghost-btn {
    background: #f1f5f9;
    padding: 10px 14px;
    border-radius: 12px;
    font-weight: 700;
    border: none;
  }

  @keyframes pop {
    from {
      transform: scale(0.95);
      opacity: 0;
    }

    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  /* ============================= */
  /* MODAL INPUT POLISH */
  /* ============================= */

  .modal-card .field {
    margin-bottom: 14px;
  }

  .modal-card input {
    width: 100%;
    margin-top: 6px;
    padding: 12px 14px;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    background: #f8fafc;
    font-weight: 600;
    outline: none;
    transition: all 0.15s ease;
  }

  .modal-card input:focus {
    border-color: #0f172a;
    background: white;
  }
</style>